import { useLanguage } from '../contexts/LanguageContext';
import { useEffect, useRef } from 'react';
import OrbitingSkills from './OrbitingSkills';


export default function MessageSection() {
    const { t } = useLanguage();
    const sectionRef = useRef<HTMLElement>(null);
    const titleRef = useRef<HTMLHeadingElement>(null);
    const descriptionRef = useRef<HTMLParagraphElement>(null);
    const messageRefs = useRef<(HTMLDivElement | null)[]>([]);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        // Animate title
                        if (entry.target === titleRef.current) {
                            entry.target.classList.add('animate-fade-in-up');
                        }
                        // Animate description
                        if (entry.target === descriptionRef.current) {
                            setTimeout(() => {
                                entry.target.classList.add('animate-fade-in-up');
                            }, 200);
                        }
                        // Animate messages with staggered delay
                        if (messageRefs.current.includes(entry.target as HTMLDivElement)) {
                            const index = messageRefs.current.indexOf(entry.target as HTMLDivElement);
                            setTimeout(() => {
                                entry.target.classList.add('animate-fade-in-up');
                            }, 400 + (index * 200)); // 400ms base delay + 200ms per item
                        }
                    }
                });
            },
            { threshold: 0.1 }
        );

        // Observe all elements
        if (titleRef.current) observer.observe(titleRef.current);
        if (descriptionRef.current) observer.observe(descriptionRef.current);
        messageRefs.current.forEach((message) => {
            if (message) observer.observe(message);
        });

        return () => observer.disconnect();
    }, []);

    return (
        <section ref={sectionRef} className='text-white' style={{ backgroundColor: 'rgba(36, 68, 115, 0.75)' }}>
            <h1 
                ref={titleRef}
                className='text-4xl md:text-6xl font-semibold md:py-10 py-5 text-center opacity-0 translate-y-8 transition-all duration-700 ease-out hover:scale-105 hover:text-blue-600 cursor-pointer' 
            >
                {t('home.message.title')}
            </h1>
            <p 
                ref={descriptionRef}
                className='text-lg md:text-xl py-5 md:py-8 md:px-8 px-2 opacity-0 translate-y-8 transition-all duration-700 ease-out hover:text-blue-200 cursor-pointer' 
            >
                {t('home.message.description')}
            </p>
            <div className='md:hidden mb-8'>
                <OrbitingSkills />
            </div>
            <div className='grid grid-cols-1 md:grid-cols-2 h-full'>
                <div className='md:h-full md:px-5 px-2 pb-5' >
                    <div className='flex flex-col h-[70%] justify-around md:gap-[6rem] gap-8'>
                        <div 
                            ref={(el) => { messageRefs.current[0] = el; }}
                            className='flex items-center opacity-0 translate-y-8 transition-all duration-700 ease-out hover:scale-105 hover:translate-x-2 group cursor-pointer'
                        >
                            
                            <p className='text-lg md:text-xl group-hover:text-blue-200 transition-colors duration-300 ml-4'>
                                {t('home.message.messages.message1').split('\n').map((line, index) => (
                                    <span key={index} className={`${index === 0 ? 'font-bold' : ''}`}>
                                        {line + ' '}
                                    </span>
                                ))}
                            </p>
                        </div>
                        <div 
                            ref={(el) => { messageRefs.current[1] = el; }}
                            className='flex items-center opacity-0 translate-y-8 transition-all duration-700 ease-out hover:scale-105 hover:translate-x-2 group cursor-pointer'
                        >
                            
                            <p className='text-lg md:text-xl group-hover:text-blue-200 transition-colors duration-300 ml-4'>
                                {t('home.message.messages.message2').split('\n').map((line, index) => (
                                    <span key={index} className={`${index === 0 ? 'font-bold' : ''}`}>
                                        {line + ' '}
                                    </span>
                                ))}
                            </p>
                        </div>
                        <div 
                            ref={(el) => { messageRefs.current[2] = el; }}
                            className='flex items-center opacity-0 translate-y-8 transition-all duration-700 ease-out hover:scale-105 hover:translate-x-2 group cursor-pointer'
                        >
                            
                            <p className='text-lg md:text-xl group-hover:text-blue-200 transition-colors duration-300 ml-4'>
                                {t('home.message.messages.message3').split('\n').map((line, index) => (
                                    <span key={index} className={`${index === 0 ? 'font-bold' : ''}`}>
                                        {line + ' '}
                                    </span>
                                ))}
                            </p>
                        </div>
                        <div 
                            ref={(el) => { messageRefs.current[3] = el; }}
                            className='flex items-center opacity-0 translate-y-8 transition-all duration-700 ease-out hover:scale-105 hover:translate-x-2 group cursor-pointer'
                        >
                            
                            <p className='text-lg md:text-xl group-hover:text-blue-200 transition-colors duration-300 ml-4'>
                                {t('home.message.messages.message4').split('\n').map((line, index) => (
                                    <span key={index} className={`${index === 0 ? 'font-bold' : ''}`}>
                                        {line + ' '}
                                    </span>
                                ))}
                            </p>
                        </div>
                    </div>
                </div>
                <div className='hidden md:block md:h-full'>
                    <OrbitingSkills />
                </div>
            </div>
        </section>
    )
}